import { apiRequest } from '../lib/api-client';

/** The tenant's own settings (context.md sections 130, 132). Phase 40. */

export interface BusinessHours {
  /** Minutes from midnight, in the tenant's own timezone. */
  startMinute: number;
  endMinute: number;
  /** Weekdays worked; 0 is Sunday. */
  days: number[];
}

/** What a message does to a conversation that was already resolved. */
export interface InboxReopenRule {
  policy: 'REOPEN' | 'NEW';
  windowMinutes: number;
}

export interface TenantSettings {
  id: string;
  name: string;
  slug: string;
  timezone: string;
  locale: string;
  currency: string;
  businessHours: BusinessHours;
  inboxReopen: InboxReopenRule;
  /**
   * When a conversation the bot is handling closes by itself (Phase 109).
   *
   * `minutes` null means never, which is the default: a silence is not by itself a finished
   * conversation.
   */
  inboxBotClosing: { minutes: number | null; message: string | null };
  /** Whether replies carry the sender's name, and in what shape (Phase 113). */
  inboxSignature: { enabled: boolean; format: string };
  /** The tenant's own face (Phase 118). */
  branding: Branding;
  /** What the company means to sell in a month, in cents; null when nobody set one (Phase 120). */
  salesTargetCents: number | null;
}

export interface BrandPalette {
  accent: string;
  accentStrong: string;
  accentSoft: string;
  accentText: string;
}

export interface Branding {
  color: string | null;
  /** Derived on the server, so the browser does no colour arithmetic. */
  palette: BrandPalette | null;
  logoFileId: string | null;
  customCss: string | null;
}

export function getBranding(): Promise<Branding> {
  return apiRequest('/v1/tenant/branding');
}

export function updateBranding(body: {
  color?: string | null;
  customCss?: string | null;
}): Promise<Branding> {
  return apiRequest('/v1/tenant/branding', { method: 'PUT', body });
}

export function uploadLogo(body: {
  filename: string;
  mimeType: string;
  content: string;
}): Promise<Branding> {
  return apiRequest('/v1/tenant/branding/logo', { method: 'PUT', body });
}

export function removeLogo(): Promise<Branding> {
  return apiRequest('/v1/tenant/branding/logo', { method: 'DELETE' });
}

export const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6] as const;

export function getTenantSettings(): Promise<TenantSettings> {
  return apiRequest('/v1/tenant');
}

export function listTenantOptions(): Promise<{
  timezones: string[];
  locales: string[];
  currencies: string[];
}> {
  return apiRequest('/v1/tenant/options');
}

export function updateTenantSettings(input: {
  name?: string;
  timezone?: string;
  locale?: string;
  currency?: string;
  businessHours?: BusinessHours;
  inboxReopen?: InboxReopenRule;
  inboxBotClosing?: { minutes: number | null; message?: string | null };
  inboxSignature?: { enabled: boolean; format?: string };
  /** Null clears the target; absent leaves it alone. */
  salesTargetCents?: number | null;
}): Promise<TenantSettings> {
  return apiRequest('/v1/tenant', { method: 'PATCH', body: input });
}

/** `09:00` from 540, for an `<input type="time">`. */
export function asClock(minutes: number): string {
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;

  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

/** The inverse, refusing anything that is not a real time of day. */
export function fromClock(value: string): number | null {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(value.trim());

  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
}
